/* DetailPopovers — Kind + Medewerker detail-popovers voor de rooster grid.
   Extracted uit RoosterPage.jsx (feb 2026 split). */
import React from "react";
import api from "@/lib/api";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Badge } from "@/components/ui/badge";

export function KindDetailPopover({ kindId, weekStart, children }) {
  const [open, setOpen] = React.useState(false);
  const [data, setData] = React.useState(null);
  React.useEffect(() => {
    if (!open || data) return;
    api.get(`/planning/kind-detail/${kindId}`, { params: { week_start: weekStart } })
      .then(r => setData(r.data)).catch(() => setData({ error: true }));
  }, [open, kindId, weekStart, data]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent align="start" className="w-80 p-0" data-testid={`kind-popover-${kindId}`}>
        {!data && <div className="p-3 text-xs text-neutral-500">laden…</div>}
        {data && data.error && <div className="p-3 text-xs text-red-700">kon detail niet laden</div>}
        {data && !data.error && (
          <div>
            <div className="px-3 py-2 border-b border-neutral-100 bg-amber-50/40">
              <div className="text-sm font-semibold text-neutral-900">
                {data.kind.voornaam} {data.kind.achternaam}
              </div>
              {data.kind.geboortedatum && (
                <div className="text-[11px] text-neutral-600">
                  {(() => {
                    try {
                      const gd = new Date(data.kind.geboortedatum);
                      const now = new Date();
                      const totalM = (now.getFullYear() - gd.getFullYear()) * 12 + (now.getMonth() - gd.getMonth());
                      return `${Math.floor(totalM / 12)}j ${totalM % 12}m · geb. ${data.kind.geboortedatum}`;
                    } catch { return data.kind.geboortedatum; }
                  })()}
                </div>
              )}
            </div>
            <div className="p-3 space-y-2 text-[11px]">
              {data.mentor && (
                <div>
                  <div className="text-[10px] uppercase tracking-widest font-semibold text-neutral-500">Mentor</div>
                  <div className="text-neutral-800">{data.mentor.naam}</div>
                </div>
              )}
              {data.kind.allergieen && data.kind.allergieen.length > 0 && (
                <div>
                  <div className="text-[10px] uppercase tracking-widest font-semibold text-red-700">Allergieën ⚠</div>
                  <div className="text-red-800 font-medium">{data.kind.allergieen.join(", ")}</div>
                </div>
              )}
              {data.kind.medicatie && data.kind.medicatie.length > 0 && (
                <div>
                  <div className="text-[10px] uppercase tracking-widest font-semibold text-blue-700">Medicatie</div>
                  <div className="text-blue-800">{data.kind.medicatie.join(", ")}</div>
                </div>
              )}
              {data.kind.bijzonderheden && (
                <div>
                  <div className="text-[10px] uppercase tracking-widest font-semibold text-neutral-500">Bijzonderheden</div>
                  <div className="text-neutral-800">{data.kind.bijzonderheden}</div>
                </div>
              )}
              {data.ouders && data.ouders.length > 0 && (
                <div>
                  <div className="text-[10px] uppercase tracking-widest font-semibold text-neutral-500">Ouder(s)</div>
                  {data.ouders.map(o => (
                    <div key={o.id} className="text-neutral-800">
                      <div className="font-medium">{o.naam}</div>
                      {o.telefoon && <a href={`tel:${o.telefoon}`} className="text-emerald-700 hover:underline">📞 {o.telefoon}</a>}
                      {o.email && <a href={`mailto:${o.email}`} className="block text-blue-700 hover:underline truncate">✉ {o.email}</a>}
                      {o.noodcontact && <div className="text-red-700 text-[10px]">noodcontact: {o.noodcontact}</div>}
                    </div>
                  ))}
                </div>
              )}
              <div>
                <div className="text-[10px] uppercase tracking-widest font-semibold text-neutral-500">Aanwezigheid deze week</div>
                {data.aanwezigheid_week.length === 0 && <div className="text-neutral-400">— geen meldingen</div>}
                {data.aanwezigheid_week.map((p) => (
                  <div key={`${p.datum}-${p.dagdeel}`} className={p.aanwezig === false ? "text-neutral-500" : "text-emerald-800"}>
                    {p.datum} {p.dagdeel}: {p.aanwezig === false ? `afwezig (${p.absent_reason || "?"})` : "aanwezig"}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}


export function MedewerkerDetailPopover({ mwId, weekStart, children }) {
  const [open, setOpen] = React.useState(false);
  const [data, setData] = React.useState(null);
  React.useEffect(() => {
    if (!open || data) return;
    api.get(`/planning/medewerker-detail/${mwId}`, { params: { week_start: weekStart } })
      .then(r => setData(r.data)).catch(() => setData({ error: true }));
  }, [open, mwId, weekStart, data]);

  const isExpired = (iso) => {
    if (!iso) return false;
    try { return new Date(iso) < new Date(); } catch { return false; }
  };
  const daysUntil = (iso) => {
    if (!iso) return null;
    try {
      const diff = (new Date(iso) - new Date()) / (1000 * 60 * 60 * 24);
      return Math.floor(diff);
    } catch { return null; }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent align="start" className="w-80 p-0" data-testid={`mw-popover-${mwId}`}>
        {!data && <div className="p-3 text-xs text-neutral-500">laden…</div>}
        {data && data.error && <div className="p-3 text-xs text-red-700">kon detail niet laden</div>}
        {data && !data.error && (
          <div>
            <div className="px-3 py-2 border-b border-neutral-100 bg-emerald-50/40">
              <div className="text-sm font-semibold text-neutral-900">{data.medewerker.naam}</div>
              {data.medewerker.functie && (
                <div className="text-[11px] text-neutral-600">{data.medewerker.functie}</div>
              )}
            </div>
            <div className="p-3 space-y-2 text-[11px]">
              <div className="flex items-center justify-between">
                <div className="text-[10px] uppercase tracking-widest font-semibold text-neutral-500">Week-uren</div>
                <div className="text-neutral-900 font-bold tabular-nums">
                  {data.week_uren}h {data.medewerker.contract_uren > 0 && <span className="opacity-70">/ {data.medewerker.contract_uren}h</span>}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2 text-[11px]">
                <div>
                  <div className="text-[10px] uppercase tracking-widest font-semibold text-neutral-500">Diploma PW</div>
                  <div className={data.medewerker.gediplomeerd ? "text-emerald-800 font-medium" : "text-red-700"}>
                    {data.medewerker.gediplomeerd ? "✓ " + (data.medewerker.diploma_type || "PW") : "✗ ontbreekt"}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest font-semibold text-neutral-500">EHBO</div>
                  <div className={data.medewerker.heeft_ehbo ? "text-emerald-800 font-medium" : "text-neutral-500"}>
                    {data.medewerker.heeft_ehbo ? "✓ geldig" : "—"}
                  </div>
                  {data.medewerker.ehbo_vervaldatum && (
                    <div className={`text-[10px] ${isExpired(data.medewerker.ehbo_vervaldatum) ? "text-red-700 font-medium" : "text-neutral-500"}`}>
                      vervalt {data.medewerker.ehbo_vervaldatum}
                    </div>
                  )}
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest font-semibold text-neutral-500">VOG</div>
                  {data.medewerker.vog_vervaldatum ? (
                    <>
                      <div className={isExpired(data.medewerker.vog_vervaldatum) ? "text-red-700 font-bold" : "text-emerald-800"}>
                        {isExpired(data.medewerker.vog_vervaldatum) ? "⚠ verlopen" : "geldig"}
                      </div>
                      <div className="text-[10px] text-neutral-500">
                        {isExpired(data.medewerker.vog_vervaldatum)
                          ? `sinds ${data.medewerker.vog_vervaldatum}`
                          : (() => { const d = daysUntil(data.medewerker.vog_vervaldatum); return d !== null && d < 60 ? `nog ${d} dagen` : `tot ${data.medewerker.vog_vervaldatum}`; })()}
                      </div>
                    </>
                  ) : (
                    <div className="text-red-700 font-medium">✗ niet ingevuld</div>
                  )}
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest font-semibold text-neutral-500">Babytraining</div>
                  <div className={data.medewerker.heeft_babytraining ? "text-pink-800 font-medium" : "text-neutral-500"}>
                    {data.medewerker.heeft_babytraining ? "✓" : "—"}
                  </div>
                </div>
              </div>
              {(data.medewerker.voorkeur_opener || data.medewerker.voorkeur_sluiter) && (
                <div className="flex items-center gap-2 pt-1 border-t border-neutral-100">
                  {data.medewerker.voorkeur_opener && <Badge variant="outline" className="text-[10px] bg-blue-50 text-blue-700 border-blue-200">opener</Badge>}
                  {data.medewerker.voorkeur_sluiter && <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-700 border-amber-200">sluiter</Badge>}
                </div>
              )}
              {(data.medewerker.telefoon || data.medewerker.email) && (
                <div className="pt-1 border-t border-neutral-100">
                  {data.medewerker.telefoon && <a href={`tel:${data.medewerker.telefoon}`} className="block text-emerald-700 hover:underline">📞 {data.medewerker.telefoon}</a>}
                  {data.medewerker.email && <a href={`mailto:${data.medewerker.email}`} className="block text-blue-700 hover:underline truncate">✉ {data.medewerker.email}</a>}
                </div>
              )}
            </div>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
